/*
 * This work has been created or enhanced by
 *   Markus Luckey, 2025, KERNBLICK GmbH
 *
 * This file is covered by the EU PL 1.2 license.
 * You may obtain a copy of the licence at
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 */

import * as Oidc from 'oidc-client-ts';
import 'babel-polyfill';
import EnvProvider from '../EnvProvider.js';

function trailingSlash(value) { return (value === undefined) ? value : value.replace(/\/?$/, '/') }

Oidc.Log.setLogger(console);
Oidc.Log.setLevel(Oidc.Log.INFO);

const authority = trailingSlash(EnvProvider.value('OIDC_AUTHORITY'));
let mgr = null;

if (authority) {
  mgr = new Oidc.UserManager({
    userStore: new Oidc.WebStorageStateStore(),
    authority: authority,
    client_id: EnvProvider.value('OIDC_CLIENT_ID') || 'client-id',
    redirect_uri: window.location.origin + '/callback',
    response_type: 'code',
    scope: 'openid profile email',
    post_logout_redirect_uri: window.location.origin + '/loggedout',
    silent_redirect_uri: window.location.origin + '/silent-renew',
    automaticSilentRenew: true,
    loadUserInfo: true
  });

  mgr.events.addUserLoaded(function (user) {
    console.debug('New User Loaded：', arguments);
    console.debug('Acess_token: ', user.access_token)
  });

  mgr.events.addAccessTokenExpiring(function () {
    console.debug('AccessToken Expiring：', arguments);
  });

  mgr.events.addAccessTokenExpired(function () {
    console.debug('AccessToken Expired：', arguments);
    // alert('Session expired. Going out!');
    mgr.signoutRedirect().then(function (resp) {
      console.debug('signed out', resp);
    }).catch(function (err) {
      console.log(err)
    })
  });

  mgr.events.addSilentRenewError(function () {
    console.error('Silent Renew Error：', arguments);
  });

  mgr.events.addUserSignedOut(function () {
    alert('Going out!');
    console.debug('UserSignedOut：', arguments);
    mgr.signoutRedirect().then(function (resp) {
      console.debug('signed out', resp);
    }).catch(function (err) {
      console.log(err)
    })
  });

  mgr.events.addUserUnloaded(() => {
    console.debug('User logged out');
  });
}

else {
  console.debug('OIDC Authority not set. Authorization is disabled.');
}


export default class SecurityService {

  getUserManager () {
    return mgr;
  }

  isAuthorizationEnabled () {
    return (mgr !== null);
  }

  // Renew the token manually
  renewToken () {
    if (!mgr) return Promise.reject(null);

    let self = this
    return new Promise((resolve, reject) => {
      mgr.signinSilent().then(function (user) {
        if (user == null) {
          return self.signIn()
        } else{
          return resolve(user)
        }
      }).catch(function (err) {
        console.log(err)
        return reject(err)
      });
    })
  }

  // Get the user who is logged in
  getUser () {
    if (!mgr) return Promise.reject(null);

    console.debug('Getting user')
    let self = this
    return new Promise((resolve, reject) => {
      mgr.getUser().then(function (user) {
        if (user == null) {
          return resolve(null)
        } else{
          return resolve(user)
        }
      }).catch(function (err) {
        console.log(err)
        return reject(err)
      });
    })
  }

  // Check if there is any user logged in
  getSignedIn () {
    if (!mgr) return Promise.reject(true);

    console.debug('Checking if user is signed in')
    let self = this
    return new Promise((resolve, reject) => {
      mgr.getUser().then(function (user) {
        if (user == null) {
          return resolve(false)
        } else{
          return resolve(true)
        }
      }).catch(function (err) {
        console.log(err)
        return reject(err)
      });
    })
  }

  // Redirect of the current window to the authorization endpoint.
  signIn () {
    if (!mgr) return;

    console.debug('Signing in')
    return mgr.signinRedirect().catch(function (err) {
      console.log(err)
    })
  }

  async completeSignIn () {
    if (!mgr) return Promise.reject(null);

    console.debug('Completing sign in')
    let user = await mgr.signinCallback()
    console.debug('User signed in:', user);
    return user
  };

  // Redirect of the current window to the end session endpoint
  signOut () {
    if (!mgr) return;

    console.debug('Signing out')
    mgr.signoutRedirect().then(function (resp) {
      console.debug('signed out', resp);
    }).catch(function (err) {
      console.log(err)
    })
  }

  async completeSignOut () {
    if (!mgr) return Promise.reject(null);

    console.debug('Completing sign out')
    let user = await mgr.signoutRedirectCallback()
    console.debug('User signed out:', user);
    return user
  };

  // Get the profile of the user logged in
  getProfile () {
    if (!mgr) return Promise.reject(null);

    console.debug('Getting profile')
    let self = this
    return new Promise((resolve, reject) => {
      mgr.getUser().then(function (user) {
        if (user == null) {
          self.signIn()
          return resolve(null)
        } else{
          return resolve(user.profile)
        }
      }).catch(function (err) {
        console.log(err)
        return reject(err)
      });
    })
  }

  // Get the token id
  getIdToken(){
    if (!mgr) return Promise.reject(null);

    console.debug('Getting id token')
    let self = this
    return new Promise((resolve, reject) => {
      mgr.getUser().then(function (user) {
        if (user == null) {
          self.signIn()
          return resolve(null)
        } else{
          return resolve(user.id_token)
        }
      }).catch(function (err) {
        console.log(err)
        return reject(err)
      });
    })
  }

  // Get the session state
  getSessionState(){
    if (!mgr) return Promise.reject(null);

    console.debug('Getting session state')
    let self = this
    return new Promise((resolve, reject) => {
      mgr.getUser().then(function (user) {
        if (user == null) {
          self.signIn()
          return resolve(null)
        } else{
          return resolve(user.session_state)
        }
      }).catch(function (err) {
        console.log(err)
        return reject(err)
      });
    })
  }

  // Get the access token of the logged in user
  getAccessToken(){
    if (!mgr) return Promise.reject(null);

    console.debug('Getting access token')
    let self = this
    return new Promise((resolve, reject) => {
      mgr.getUser().then(function (user) {
        if (user == null) {
          self.signIn()
          return resolve(null)
        } else{
          return resolve(user.access_token)
        }
      }).catch(function (err) {
        console.log(err)
        return reject(err)
      });
    })
  }

  // Takes the scopes of the logged in user
  getScopes(){
    if (!mgr) return Promise.reject(null);

    console.debug('Getting scopes')
    let self = this
    return new Promise((resolve, reject) => {
      mgr.getUser().then(function (user) {
        if (user == null) {
          self.signIn()
          return resolve(null)
        } else{
          return resolve(user.scopes)
        }
      }).catch(function (err) {
        console.log(err)
        return reject(err)
      });
    })
  }

  // Get the user roles logged in
  getRoles () {
    if (!mgr) return Promise.reject(null);

    console.debug('Getting roles')
    let self = this
    return new Promise((resolve, reject) => {
      mgr.getUser().then(function (user) {
        if (user == null) {
          self.signIn()
          return resolve(null)
        } else{
          let roles = user.profile.role ? [user.profile.role] : user.profile.roles
          let groups = user.profile.group ? [user.profile.group] : user.profile.groups
          return resolve(roles || groups)
        }
      }).catch(function (err) {
        console.log(err)
        return reject(err)
      });
    })
  }
}
